import type { PlanChangeRetention } from './planRetention.js';
import { UserRole } from '../types/index.js';

export type BillingPlanId =
  | 'free_driver'
  | 'family_trackside'
  | 'privateer'
  | 'starter_team'
  | 'growth_team'
  | 'pro_team'
  | 'academy'
  | 'event_pass'
  | 'enterprise';

export type BillingStatus = 'active' | 'trialing' | 'past_due' | 'comp';
export type StripeSubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'paused'
  | 'trialing'
  | 'unpaid';

export interface PlanEntitlements {
  maxDrivers: number | null;
  maxVehicles: number | null;
  /** Owner, coach, and mechanic accounts on club teams. */
  maxStaffUsers: number | null;
  /** Parent/guardian login accounts on family workspaces. */
  maxParents: number | null;
  /** Maximum seat pool on the plan (entitlement cap, not a billing allotment). */
  includedSeats: number | null;
  platformFeeMonthly: number;
  seatPriceMonthly: number;
  maxLocations: number | null;
  telemetryRetentionDays: number | null;
  mediaStorageGb: number | null;
  aiCreditsMonthly: number;
  advancedTelemetry: boolean;
  aiCoaching: boolean;
  parentCommunications: boolean;
  /** Log service, maintenance queue, and service intervals. */
  maintenanceRecords: boolean;
  /** Edit kart setup, apply setup history, and manage vehicle configuration. */
  vehicleSetupManagement: boolean;
  inventoryManagement: boolean;
  whiteLabelBranding: boolean;
  multiLocation: boolean;
  apiAccess: boolean;
  prioritySupport: boolean;
}

export interface TeamPlanUsage {
  drivers: number;
  vehicles: number;
  /** Owner, coach, and mechanic accounts counted against `maxStaffUsers`. */
  staffUsers: number;
  teamOwners: number;
  coaches: number;
  mechanics: number;
  parents: number;
  /** Club teams: only team-managed drivers bill as seats. Parent-linked drivers do not. */
  teamManagedDrivers?: number;
  /** Club teams: staff seats that bill on the team subscription (never parent-only accounts). */
  billableStaffUsers?: number;
  /** Club teams: parents linked to their own family workspace subscription. */
  parentsWithOwnFamilyBilling?: number;
  /** Club teams: parents on the roster without a separate family billing workspace. */
  parentsOnTeamOnly?: number;
  /** Family workspaces: active user accounts on the team (one seat per login). */
  memberAccounts?: number;
}

export interface TeamEntitlementsSnapshot {
  planId: BillingPlanId;
  planLabel: string;
  planDescription: string;
  billingStatus: BillingStatus;
  trialEndsAt: string | null;
  entitlements: PlanEntitlements;
  billingAddons: BillingAddonId[];
  usage: TeamPlanUsage;
  isFamilyTeamWorkspace?: boolean;
  seatUsage?: number;
  estimatedMonthlyTotal?: number;
  /** True when the API has Stripe credentials configured. */
  stripeAvailable?: boolean;
  /** True when the team workspace has a Stripe subscription on file. */
  hasStripeSubscription?: boolean;
  /** Stripe's raw status, used to choose between portal recovery and a fresh checkout. */
  stripeSubscriptionStatus?: StripeSubscriptionStatus | null;
  /** True after this workspace has started its one allowed Stripe trial. */
  hasUsedStripeTrial?: boolean;
  /** True when the workspace is on an active partner / design-partner comp program. */
  isPartnerComp?: boolean;
  /** Label for the active partner program, when applicable. */
  partnerProgramLabel?: string | null;
  /** True when the workspace must choose a paid plan (e.g. after partner code revoked). */
  requiresPaidPlan?: boolean;
  /** Live Stripe catalog rates for launch tiers (when API has catalog configured). */
  launchPlanPricing?: Partial<Record<LaunchBillingPlanId, LaunchPlanPricing>>;
  /** Monthly total from Stripe upcoming invoice preview, when subscribed. */
  stripeMonthlyTotal?: number | null;
  /** Current recurring Stripe subscription line items, when subscribed. */
  stripeMonthlyLines?: PlanCostBreakdownLine[] | null;
  /** Family workspace id when this club team was upgraded from Essentials. */
  originFamilyTeamId?: string | null;
  /** True when switching back to Essentials is supported (data retained in archived club team). */
  canRevertToFamily?: boolean;
}

/** Feature fields available to every team member. Billing and usage stay private. */
export type TeamFeatureEntitlements = Pick<
  TeamEntitlementsSnapshot,
  'entitlements' | 'isFamilyTeamWorkspace'
>;

/** GET /teams/current/entitlements includes billing/usage only for administrators. */
export type TeamEntitlementsResponse = TeamFeatureEntitlements & Partial<TeamEntitlementsSnapshot>;

export type LaunchPlanPricing = {
  platformFeeMonthly: number;
  seatPriceMonthly: number;
  source?: 'stripe' | 'default';
};

export type PlanPricingOverride = Pick<LaunchPlanPricing, 'platformFeeMonthly' | 'seatPriceMonthly'>;

export type PlanChangePreviewLineKind = 'recurring' | 'proration' | 'credit' | 'adjustment' | 'tax' | 'other';

export type PlanChangePreviewLine = {
  label: string;
  amount: number;
  kind: PlanChangePreviewLineKind;
};

export type PlanChangePreview = {
  planId: LaunchBillingPlanId;
  planLabel: string;
  mode: 'new_subscription' | 'subscription_update';
  partnerComp?: boolean;
  seatCount: number;
  monthlyTotal: number;
  monthlyLines: PlanCostBreakdownLine[];
  dueToday: number;
  dueTodayLines: PlanChangePreviewLine[];
  hasProration: boolean;
  workspaceConversion?: {
    willConvert: boolean;
    direction?: 'upgrade' | 'downgrade';
    reactivatesArchivedClubTeam?: boolean;
    summary: string;
  };
  retention?: PlanChangeRetention;
};

export type PlanUsageRowMode = 'limit' | 'count';

export type PlanUsageRow = {
  label: string;
  used: number;
  limit: number | null;
  mode: PlanUsageRowMode;
};

export type BillingPlanDefinition = {
  id: BillingPlanId;
  label: string;
  description: string;
  entitlements: PlanEntitlements;
};

const UNIFIED_LAUNCH_PRICING = {
  platformFeeMonthly: 29,
  seatPriceMonthly: 10,
} as const;

/** Short pricing line shown across web billing surfaces. */
export const LAUNCH_PRICING_TAGLINE = '$29/mo platform + $10/seat. Everything included.';

const STARTER_TEAM: PlanEntitlements = {
  maxDrivers: null,
  maxVehicles: null,
  maxStaffUsers: 5,
  maxParents: null,
  includedSeats: null,
  platformFeeMonthly: UNIFIED_LAUNCH_PRICING.platformFeeMonthly,
  seatPriceMonthly: UNIFIED_LAUNCH_PRICING.seatPriceMonthly,
  maxLocations: 1,
  telemetryRetentionDays: 365,
  mediaStorageGb: 25,
  aiCreditsMonthly: 0,
  advancedTelemetry: false,
  aiCoaching: false,
  parentCommunications: true,
  maintenanceRecords: true,
  vehicleSetupManagement: true,
  inventoryManagement: false,
  whiteLabelBranding: false,
  multiLocation: false,
  apiAccess: false,
  prioritySupport: false,
};

const GROWTH_TEAM: PlanEntitlements = {
  maxDrivers: null,
  maxVehicles: null,
  maxStaffUsers: 15,
  maxParents: null,
  includedSeats: 50,
  platformFeeMonthly: 219,
  seatPriceMonthly: 10,
  maxLocations: 2,
  telemetryRetentionDays: 730,
  mediaStorageGb: 100,
  aiCreditsMonthly: 50,
  advancedTelemetry: true,
  aiCoaching: false,
  parentCommunications: true,
  maintenanceRecords: true,
  vehicleSetupManagement: true,
  inventoryManagement: true,
  whiteLabelBranding: false,
  multiLocation: false,
  apiAccess: false,
  prioritySupport: false,
};

const ESSENTIALS_FAMILY: PlanEntitlements = {
  maxDrivers: null,
  maxVehicles: null,
  maxStaffUsers: 2,
  maxParents: 2,
  includedSeats: null,
  platformFeeMonthly: UNIFIED_LAUNCH_PRICING.platformFeeMonthly,
  seatPriceMonthly: UNIFIED_LAUNCH_PRICING.seatPriceMonthly,
  maxLocations: 1,
  telemetryRetentionDays: 180,
  mediaStorageGb: 10,
  aiCreditsMonthly: 0,
  advancedTelemetry: false,
  aiCoaching: false,
  parentCommunications: true,
  maintenanceRecords: false,
  vehicleSetupManagement: false,
  inventoryManagement: false,
  whiteLabelBranding: false,
  multiLocation: false,
  apiAccess: false,
  prioritySupport: false,
};

export const BILLING_PLAN_DEFINITIONS: Record<BillingPlanId, BillingPlanDefinition> = {
  free_driver: {
    id: 'free_driver',
    label: 'Free Driver',
    description: 'Basic driver profile and limited history.',
    entitlements: {
      maxDrivers: 1,
      maxVehicles: 0,
      maxStaffUsers: 0,
      maxParents: 0,
      includedSeats: 1,
      platformFeeMonthly: 0,
      seatPriceMonthly: 0,
      maxLocations: 0,
      telemetryRetentionDays: 30,
      mediaStorageGb: 1,
      aiCreditsMonthly: 0,
      advancedTelemetry: false,
      aiCoaching: false,
      parentCommunications: false,
      maintenanceRecords: false,
      vehicleSetupManagement: false,
      inventoryManagement: false,
      whiteLabelBranding: false,
      multiLocation: false,
      apiAccess: false,
      prioritySupport: false,
    },
  },
  family_trackside: {
    id: 'family_trackside',
    label: 'Family',
    description: 'Parents and linked drivers on one household subscription.',
    entitlements: ESSENTIALS_FAMILY,
  },
  privateer: {
    id: 'privateer',
    label: 'Solo driver',
    description: 'One driver managing their own vehicles and telemetry.',
    entitlements: {
      maxDrivers: 1,
      maxVehicles: null,
      maxStaffUsers: 0,
      maxParents: 0,
      includedSeats: null,
      platformFeeMonthly: UNIFIED_LAUNCH_PRICING.platformFeeMonthly,
      seatPriceMonthly: UNIFIED_LAUNCH_PRICING.seatPriceMonthly,
      maxLocations: 1,
      telemetryRetentionDays: 365,
      mediaStorageGb: 15,
      aiCreditsMonthly: 0,
      advancedTelemetry: false,
      aiCoaching: false,
      parentCommunications: false,
      maintenanceRecords: true,
      vehicleSetupManagement: true,
      inventoryManagement: true,
      whiteLabelBranding: false,
      multiLocation: false,
      apiAccess: false,
      prioritySupport: false,
    },
  },
  starter_team: {
    id: 'starter_team',
    label: 'Club team',
    description: 'Coaches, mechanics, and team-managed drivers.',
    entitlements: STARTER_TEAM,
  },
  growth_team: {
    id: 'growth_team',
    label: 'Growth Team',
    description: 'Regional teams with multiple karts and staff.',
    entitlements: GROWTH_TEAM,
  },
  pro_team: {
    id: 'pro_team',
    label: 'Pro Team',
    description: 'National programs with advanced operations.',
    entitlements: {
      maxDrivers: null,
      maxVehicles: null,
      maxStaffUsers: 30,
      maxParents: null,
      includedSeats: 100,
      platformFeeMonthly: 519,
      seatPriceMonthly: 18,
      maxLocations: 5,
      telemetryRetentionDays: null,
      mediaStorageGb: 500,
      aiCreditsMonthly: 200,
      advancedTelemetry: true,
      aiCoaching: true,
      parentCommunications: true,
      maintenanceRecords: true,
      vehicleSetupManagement: true,
      inventoryManagement: true,
      whiteLabelBranding: true,
      multiLocation: true,
      apiAccess: true,
      prioritySupport: true,
    },
  },
  academy: {
    id: 'academy',
    label: 'Academy',
    description: 'Multi-coach programs with the same full feature set.',
    entitlements: {
      maxDrivers: null,
      maxVehicles: null,
      maxStaffUsers: null,
      maxParents: null,
      includedSeats: null,
      platformFeeMonthly: UNIFIED_LAUNCH_PRICING.platformFeeMonthly,
      seatPriceMonthly: UNIFIED_LAUNCH_PRICING.seatPriceMonthly,
      maxLocations: null,
      telemetryRetentionDays: null,
      mediaStorageGb: null,
      aiCreditsMonthly: 1000,
      advancedTelemetry: true,
      aiCoaching: true,
      parentCommunications: true,
      maintenanceRecords: true,
      vehicleSetupManagement: true,
      inventoryManagement: true,
      whiteLabelBranding: true,
      multiLocation: true,
      apiAccess: true,
      prioritySupport: true,
    },
  },
  event_pass: {
    id: 'event_pass',
    label: 'Event Pass',
    description: 'Event-scoped team access.',
    entitlements: STARTER_TEAM,
  },
  enterprise: {
    id: 'enterprise',
    label: 'Enterprise',
    description: 'Contracted platform partnership.',
    entitlements: {
      maxDrivers: null,
      maxVehicles: null,
      maxStaffUsers: null,
      maxParents: null,
      includedSeats: null,
      platformFeeMonthly: 0,
      seatPriceMonthly: 0,
      maxLocations: null,
      telemetryRetentionDays: null,
      mediaStorageGb: null,
      aiCreditsMonthly: 0,
      advancedTelemetry: true,
      aiCoaching: true,
      parentCommunications: true,
      maintenanceRecords: true,
      vehicleSetupManagement: true,
      inventoryManagement: true,
      whiteLabelBranding: true,
      multiLocation: true,
      apiAccess: true,
      prioritySupport: true,
    },
  },
};

export const DEFAULT_TEAM_BILLING_PLAN_ID: BillingPlanId = 'starter_team';
export const DEFAULT_FAMILY_BILLING_PLAN_ID: BillingPlanId = 'family_trackside';
export const DEFAULT_DRIVER_BILLING_PLAN_ID: BillingPlanId = 'privateer';

/** Public launch tiers shown in upgrade messaging. */
export const LAUNCH_BILLING_PLAN_IDS = ['privateer', 'family_trackside', 'starter_team', 'academy'] as const satisfies readonly BillingPlanId[];

export type LaunchBillingPlanId = (typeof LAUNCH_BILLING_PLAN_IDS)[number];

const LAUNCH_PLAN_EXAMPLE_SEATS: Partial<Record<BillingPlanId, number>> = {
  privateer: 1,
  family_trackside: 4,
  starter_team: 6,
  academy: 12,
};

export function resolveBillingPlanId(value: string | null | undefined): BillingPlanId {
  if (value && value in BILLING_PLAN_DEFINITIONS) {
    return value as BillingPlanId;
  }

  return DEFAULT_TEAM_BILLING_PLAN_ID;
}

export function resolveTeamBillingPlanId(
  billingPlanId: string | null | undefined,
  isFamilyTeamWorkspace: boolean,
): BillingPlanId {
  if (billingPlanId && billingPlanId in BILLING_PLAN_DEFINITIONS) {
    return billingPlanId as BillingPlanId;
  }

  return isFamilyTeamWorkspace ? DEFAULT_FAMILY_BILLING_PLAN_ID : DEFAULT_TEAM_BILLING_PLAN_ID;
}

export type BillableSeatBreakdown = {
  total: number;
  staffUsers: number;
  parents: number;
  drivers: number;
  teamManagedDrivers: number;
  parentsWithOwnFamilyBilling?: number;
  parentsOnTeamOnly?: number;
};

const CLUB_STAFF_ROLES = new Set<UserRole>([UserRole.TEAM_OWNER, UserRole.COACH, UserRole.MECHANIC]);

/** True when a club-team member bills as staff (owner/coach/mechanic). Parent-only accounts never qualify. */
export function isBillableClubTeamStaffMember(
  member: Pick<{ role: UserRole; additionalRoles?: UserRole[] }, 'role' | 'additionalRoles'>,
): boolean {
  const additionalRoles = member.additionalRoles ?? [];
  if (member.role === UserRole.PARENT) {
    return additionalRoles.some((role) => CLUB_STAFF_ROLES.has(role));
  }

  return CLUB_STAFF_ROLES.has(member.role)
    || additionalRoles.some((role) => CLUB_STAFF_ROLES.has(role));
}

export function getBillableSeatBreakdown(
  usage: TeamPlanUsage,
  isFamilyTeamWorkspace: boolean,
): BillableSeatBreakdown {
  const staffUsers = usage.billableStaffUsers ?? usage.staffUsers ?? 0;
  const parents = usage.parents ?? 0;
  const drivers = usage.drivers ?? 0;
  const teamManagedDrivers = usage.teamManagedDrivers ?? 0;
  const parentsWithOwnFamilyBilling = usage.parentsWithOwnFamilyBilling ?? 0;
  const parentsOnTeamOnly = usage.parentsOnTeamOnly ?? Math.max(0, parents - parentsWithOwnFamilyBilling);

  if (isFamilyTeamWorkspace) {
    const memberAccounts = usage.memberAccounts ?? parents + drivers;
    return {
      total: memberAccounts,
      staffUsers: 0,
      parents,
      drivers,
      teamManagedDrivers: 0,
      parentsWithOwnFamilyBilling: 0,
      parentsOnTeamOnly: 0,
    };
  }

  // Club teams bill staff and team-managed drivers only. Parents and self-managed drivers pay separately.
  return {
    total: staffUsers + teamManagedDrivers,
    staffUsers,
    parents,
    drivers,
    teamManagedDrivers,
    parentsWithOwnFamilyBilling,
    parentsOnTeamOnly,
  };
}

export function countBillableSeats(usage: TeamPlanUsage, isFamilyTeamWorkspace: boolean) {
  return getBillableSeatBreakdown(usage, isFamilyTeamWorkspace).total;
}

export function estimatePlanMonthlyTotal(
  planId: BillingPlanId,
  seatCount: number,
  pricing?: PlanPricingOverride,
) {
  const { platformFeeMonthly, seatPriceMonthly } = pricing
    ?? BILLING_PLAN_DEFINITIONS[planId].entitlements;
  const normalizedSeats = seatPriceMonthly > 0 ? Math.max(1, seatCount) : Math.max(0, seatCount);
  return platformFeeMonthly + normalizedSeats * seatPriceMonthly;
}

export function formatPlanMonthlyPrice(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getLaunchPlanSummaries() {
  return LAUNCH_BILLING_PLAN_IDS.map((planId) => {
    const plan = BILLING_PLAN_DEFINITIONS[planId];
    const exampleSeats = LAUNCH_PLAN_EXAMPLE_SEATS[planId] ?? plan.entitlements.includedSeats ?? 10;
    const exampleTotal = estimatePlanMonthlyTotal(planId, exampleSeats);

    return {
      planId,
      label: plan.label,
      description: plan.description,
      platformFeeMonthly: plan.entitlements.platformFeeMonthly,
      seatPriceMonthly: plan.entitlements.seatPriceMonthly,
      includedSeats: plan.entitlements.includedSeats,
      exampleSeats,
      exampleMonthlyTotal: exampleTotal,
      breakdown: buildPlanCostBreakdown(planId, exampleSeats),
    };
  });
}

export type PlanCostBreakdownLine = {
  label: string;
  amount: number;
};

export type PlanCostBreakdown = {
  planId: BillingPlanId;
  label: string;
  description: string;
  seatCount: number;
  lines: PlanCostBreakdownLine[];
  totalMonthly: number;
};

export type LaunchPlanExample = {
  title: string;
  seatCount: number;
  seatRoles: string[];
};

export const LAUNCH_PLAN_EXAMPLES: Record<LaunchBillingPlanId, LaunchPlanExample> = {
  privateer: {
    title: 'Solo driver',
    seatCount: 1,
    seatRoles: ['Single driver account'],
  },
  family_trackside: {
    title: 'Family operation',
    seatCount: 4,
    seatRoles: ['Parents / guardians', 'Linked child drivers'],
  },
  starter_team: {
    title: 'Small club team',
    seatCount: 6,
    seatRoles: ['Staff', 'Team-managed drivers', 'Parents and self-managed drivers bill separately'],
  },
  academy: {
    title: 'Academy program',
    seatCount: 12,
    seatRoles: ['Staff', 'Team-managed drivers', 'Parents and self-managed drivers bill separately'],
  },
};

export function getLaunchPlanBillingNote(planId: LaunchBillingPlanId): string {
  if (planId === 'family_trackside') {
    return 'Bills parents and linked drivers.';
  }

  if (planId === 'privateer') {
    return 'Bills one driver account.';
  }

  return 'Bills staff and team-managed drivers. Parents and self-managed drivers pay separately.';
}

export function getLaunchPlanSeatRoleDescriptions(
  planId: LaunchBillingPlanId,
  seatPriceMonthly: number,
): string[] {
  const perSeat = `${formatPlanMonthlyPrice(seatPriceMonthly)}/seat/mo`;
  const example = LAUNCH_PLAN_EXAMPLES[planId];

  if (planId === 'family_trackside') {
    return example.seatRoles.map((role) => `${role} (${perSeat})`);
  }

  return example.seatRoles;
}

export function buildPlanCostBreakdown(
  planId: BillingPlanId,
  seatCount: number,
  pricing?: PlanPricingOverride,
): PlanCostBreakdown {
  const plan = BILLING_PLAN_DEFINITIONS[planId];
  const platformFeeMonthly = pricing?.platformFeeMonthly ?? plan.entitlements.platformFeeMonthly;
  const seatPriceMonthly = pricing?.seatPriceMonthly ?? plan.entitlements.seatPriceMonthly;
  const normalizedSeats = seatPriceMonthly > 0 ? Math.max(1, seatCount) : Math.max(0, seatCount);
  const seatSubtotal = normalizedSeats * seatPriceMonthly;
  const lines: PlanCostBreakdownLine[] = [];

  if (platformFeeMonthly > 0) {
    lines.push({ label: 'Platform fee', amount: platformFeeMonthly });
  }

  if (seatPriceMonthly > 0) {
    lines.push({
      label: normalizedSeats > 0
        ? `${normalizedSeats} seat${normalizedSeats === 1 ? '' : 's'} × ${formatPlanMonthlyPrice(seatPriceMonthly)}`
        : 'Seats',
      amount: seatSubtotal,
    });
  }

  return {
    planId,
    label: plan.label,
    description: plan.description,
    seatCount: normalizedSeats,
    lines,
    totalMonthly: platformFeeMonthly + seatSubtotal,
  };
}

export function getPlanEntitlements(planId: BillingPlanId): PlanEntitlements {
  return BILLING_PLAN_DEFINITIONS[planId].entitlements;
}

export function formatPlanLimit(value: number | null) {
  return value == null ? 'Unlimited' : String(value);
}

export function formatPlanCapability(included: boolean) {
  return included ? 'Included' : 'Not included';
}

export function planUsagePercent(used: number, limit: number | null) {
  if (limit == null || limit <= 0) {
    return null;
  }

  return Math.min(100, Math.round((used / limit) * 100));
}

export function planUsageTone(used: number, limit: number | null): 'ok' | 'warn' | 'critical' {
  const percent = planUsagePercent(used, limit);
  if (percent == null) {
    return 'ok';
  }

  if (percent >= 100) {
    return 'critical';
  }

  if (percent >= 85) {
    return 'warn';
  }

  return 'ok';
}

export function getTeamPlanUsageDescription(snapshot: TeamEntitlementsSnapshot) {
  return snapshot.planDescription;
}

export type TeamTrialStatusSummary = {
  badgeLabel: string;
  detail: string;
  daysLeft: number;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function startOfUtcDay(date: Date) {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function formatTrialEndDate(date: Date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function getTeamTrialStatusSummary(
  snapshot: Pick<TeamEntitlementsSnapshot, 'billingStatus' | 'trialEndsAt'>,
  now = new Date(),
): TeamTrialStatusSummary | null {
  if (snapshot.billingStatus !== 'trialing') {
    return null;
  }

  const trialEndDate = snapshot.trialEndsAt ? new Date(snapshot.trialEndsAt) : null;
  if (!trialEndDate || Number.isNaN(trialEndDate.getTime())) {
    return {
      badgeLabel: 'Trial active',
      detail: 'Your free trial is active.',
      daysLeft: 0,
    };
  }

  const daysLeft = Math.max(0, Math.round((startOfUtcDay(trialEndDate) - startOfUtcDay(now)) / DAY_IN_MS));
  const dayLabel = daysLeft === 1 ? 'day' : 'days';
  const endsOn = formatTrialEndDate(trialEndDate);

  if (daysLeft === 0) {
    return {
      badgeLabel: 'Ends today',
      detail: `Your free trial ends today (${endsOn}).`,
      daysLeft,
    };
  }

  return {
    badgeLabel: `${daysLeft} ${dayLabel} left`,
    detail: `${daysLeft} ${dayLabel} left in your free trial. It ends on ${endsOn}.`,
    daysLeft,
  };
}

export function getSeatUsageDetail(snapshot: TeamEntitlementsSnapshot) {
  const breakdown = getBillableSeatBreakdown(snapshot.usage, Boolean(snapshot.isFamilyTeamWorkspace));

  if (snapshot.isFamilyTeamWorkspace) {
    return `${breakdown.total} billed seat${breakdown.total === 1 ? '' : 's'}: one per account with workspace access (${breakdown.parents} parent${breakdown.parents === 1 ? '' : 's'}, ${breakdown.drivers} driver profile${breakdown.drivers === 1 ? '' : 's'}).`;
  }

  return `${breakdown.total} billed seat${breakdown.total === 1 ? '' : 's'}: ${breakdown.staffUsers} staff and ${breakdown.teamManagedDrivers} team-managed driver${breakdown.teamManagedDrivers === 1 ? '' : 's'}. Parents and self-managed drivers are not billed on this team${breakdown.parentsWithOwnFamilyBilling ? ` (${breakdown.parentsWithOwnFamilyBilling} parent${breakdown.parentsWithOwnFamilyBilling === 1 ? '' : 's'} on their own family account)` : ''}${breakdown.parentsOnTeamOnly ? ` (${breakdown.parentsOnTeamOnly} on team only)` : ''}.`;
}

export function formatSeatBillingSummary(snapshot: TeamEntitlementsSnapshot) {
  const seatCount = snapshot.seatUsage ?? getBillableSeatBreakdown(snapshot.usage, Boolean(snapshot.isFamilyTeamWorkspace)).total;
  const { platformFeeMonthly, seatPriceMonthly } = snapshot.entitlements;
  if (snapshot.stripeMonthlyLines?.length) {
    const total = snapshot.stripeMonthlyTotal
      ?? snapshot.stripeMonthlyLines.reduce((sum, line) => sum + line.amount, 0);
    const lineSummary = snapshot.stripeMonthlyLines
      .map((line) => `${line.label}: ${formatPlanMonthlyPrice(line.amount)}`)
      .join(' + ');

    return `${lineSummary} = Est. ${formatPlanMonthlyPrice(total)}/mo`;
  }

  const total = snapshot.stripeMonthlyTotal
    ?? snapshot.estimatedMonthlyTotal
    ?? estimatePlanMonthlyTotal(snapshot.planId, seatCount, snapshot.launchPlanPricing?.[snapshot.planId as LaunchBillingPlanId]);
  const platform = formatPlanMonthlyPrice(platformFeeMonthly);

  if (platformFeeMonthly <= 0 && seatPriceMonthly <= 0) {
    return `Est. ${formatPlanMonthlyPrice(total)}/mo`;
  }

  if (seatPriceMonthly <= 0 || seatCount <= 0) {
    return `${platform} platform · Est. ${formatPlanMonthlyPrice(total)}/mo`;
  }

  return `${platform} platform + ${seatCount} seats × ${formatPlanMonthlyPrice(seatPriceMonthly)} = Est. ${formatPlanMonthlyPrice(total)}/mo`;
}

export function getTeamPlanUsageRows(snapshot: TeamEntitlementsSnapshot): PlanUsageRow[] {
  const usage = {
    drivers: snapshot.usage.drivers ?? 0,
    vehicles: snapshot.usage.vehicles ?? 0,
    staffUsers: snapshot.usage.staffUsers ?? 0,
    teamOwners: snapshot.usage.teamOwners ?? 0,
    coaches: snapshot.usage.coaches ?? 0,
    mechanics: snapshot.usage.mechanics ?? 0,
    parents: snapshot.usage.parents ?? 0,
  };
  const seatUsage = snapshot.seatUsage ?? countBillableSeats(usage, Boolean(snapshot.isFamilyTeamWorkspace));

  if (snapshot.isFamilyTeamWorkspace) {
    return [
      { label: 'Parents / Guardians', used: usage.parents, limit: null, mode: 'count' },
      { label: 'Drivers', used: usage.drivers, limit: null, mode: 'count' },
      { label: 'Billed seats', used: seatUsage, limit: null, mode: 'count' },
      { label: 'Vehicles', used: usage.vehicles, limit: null, mode: 'count' },
    ];
  }

  return [
    { label: 'Team owners', used: usage.teamOwners, limit: null, mode: 'count' },
    { label: 'Coaches', used: usage.coaches, limit: null, mode: 'count' },
    { label: 'Mechanics', used: usage.mechanics, limit: null, mode: 'count' },
    { label: 'Parents / Guardians', used: usage.parents, limit: null, mode: 'count' },
    { label: 'Drivers', used: usage.drivers, limit: null, mode: 'count' },
    { label: 'Billed seats', used: seatUsage, limit: null, mode: 'count' },
    { label: 'Vehicles', used: usage.vehicles, limit: null, mode: 'count' },
  ];
}

export function teamPlanUsageNearLimit(_snapshot: TeamEntitlementsSnapshot) {
  return false;
}

export type BillingAddonId = 'maintenance_records';

export const BILLING_ADDON_DEFINITIONS: Record<
  BillingAddonId,
  { label: string; description: string; entitlements: Pick<PlanEntitlements, 'maintenanceRecords'> }
> = {
  maintenance_records: {
    label: 'Maintenance Management',
    description: 'Service logging, intervals, and the maintenance queue.',
    entitlements: { maintenanceRecords: true },
  },
};

export function normalizeBillingAddons(addons: readonly string[] | null | undefined): BillingAddonId[] {
  if (!addons?.length) {
    return [];
  }

  return addons.filter((addon): addon is BillingAddonId => addon in BILLING_ADDON_DEFINITIONS);
}

export function applyBillingAddons(base: PlanEntitlements, addons: readonly BillingAddonId[]): PlanEntitlements {
  if (addons.length === 0) {
    return base;
  }

  return addons.reduce<PlanEntitlements>((entitlements, addonId) => ({
    ...entitlements,
    ...BILLING_ADDON_DEFINITIONS[addonId].entitlements,
  }), { ...base });
}

/** Effective maintenance access from entitlements, with a family-safe loading fallback. */
export function resolveMaintenanceRecordsAccess(options: {
  entitlements?: Pick<PlanEntitlements, 'maintenanceRecords'> | null;
  isFamilyTeamWorkspace?: boolean;
}): boolean {
  if (options.entitlements) {
    return options.entitlements.maintenanceRecords;
  }

  return !Boolean(options.isFamilyTeamWorkspace);
}

export function formatPlanEntitlementAvailability(included: boolean, availableAsAddon = false) {
  if (included) {
    return 'Included';
  }

  if (availableAsAddon) {
    return 'Add-on';
  }

  return 'Not included';
}

